import React from 'react';
import {
	Edit,
	SimpleForm,
	DateInput,
	DateTimeInput,
	ReferenceInput,
	SelectInput,
	BooleanInput,
	TextInput,
	NumberInput,
	required
} from 'react-admin';
import PropTypes from 'prop-types';
import { Grid } from '../../components/wrapper';
import {
	coreWalletLimitContextsForSelection,
	coreWalletLimitTypesForSelection,
	coreWalletLimitCurrencyTypesForSelection,
	coreWalletLimitSubtypesForSelection,
	coreWalletLimitScopesForSelection,
	coreWalletLimitPeriodsForSelection,
	coreWalletLimitOperationsForSelection
} from './LimitDefinitionConstants';
import { countriesForSelection } from '../../i18n/en.js';
import { coreWalletTypesForSelection } from '../wallets/WalletConstants';
import { coreWalletVerificationLevelsForSelection } from '../users/UserConstants';
import { toApiDateTimeFormat, toJavaScriptDateFormat } from "../../components/helper/DateHelper";

const subsidiaryRenderer = choice => `${choice.id}: ${choice.name}`;
const limitGroupRenderer = choice => `${choice.id}: ${choice.description}`;

const LimitDefinitionEdit = ({
	limitContextsForSelection,
	walletTypesForSelection,
	verificationLevelsForSelection,
	limitTypesForSelection,
	limitCurrencyTypesForSelection,
	limitSubtypesForSelection,
	limitScopesForSelection,
	limitPeriodsForSelection,
	limitOperationsForSelection,
	...props
}) => (
	<Edit {...props}>
		<SimpleForm variant="standard">
			<Grid container spacing={2}>
				<Grid item xs>
					<TextInput source="id" fullWidth disabled />
					<DateTimeInput source="creationDateTime" disabled fullWidth />
					<ReferenceInput source="subsidiaryId" reference="subsidiaries">
						<SelectInput optionText={subsidiaryRenderer} fullWidth />
					</ReferenceInput>
					<ReferenceInput source="limitGroupId" reference="limitGroups">
						<SelectInput optionText={limitGroupRenderer} resettable fullWidth />
					</ReferenceInput>
					<SelectInput choices={limitContextsForSelection} source="limitContext" validate={required()} fullWidth />
					<ReferenceInput source="limitCurrency" reference="currencies">
						<SelectInput optionText="currencyCode" resettable fullWidth />
					</ReferenceInput>
					<SelectInput choices={countriesForSelection} source="walletCountry" resettable fullWidth />
					<SelectInput choices={walletTypesForSelection} source="walletType" resettable fullWidth />
					<SelectInput choices={verificationLevelsForSelection} source="userVerificationLevel" resettable fullWidth />
					<DateInput
						source="validFrom"
						fullWidth
						validate={required()}
						parse={value => toApiDateTimeFormat(value)}
						format={value => toJavaScriptDateFormat(value)}
					/>
					<DateInput
						source="validUntil"
						resettable
						fullWidth
						parse={value => toApiDateTimeFormat(value)}
						format={value => toJavaScriptDateFormat(value)}
					/>
					<BooleanInput source="enabled" />
				</Grid>
				<Grid item xs>
					<SelectInput choices={limitTypesForSelection} source="limitType" validate={required()} fullWidth />
					<SelectInput choices={limitCurrencyTypesForSelection} source="limitCurrencyType" validate={required()} fullWidth />
					<SelectInput choices={limitSubtypesForSelection} source="limitSubtype" validate={required()} fullWidth />
					<SelectInput choices={limitScopesForSelection} source="limitScope" validate={required()} fullWidth />
					<TextInput source="limitPaymentMethod" fullWidth />
					<SelectInput choices={limitPeriodsForSelection} source="limitPeriod" validate={required()} fullWidth />
					<SelectInput choices={limitOperationsForSelection} source="limitOperation" validate={required()} fullWidth />
					<NumberInput source="limitPeriodAmount" validate={required()} fullWidth />
					<NumberInput source="limitValue" fullWidth />
					<Grid container spacing={24}>
						<Grid item xs>
							<NumberInput source="limitValueAmount.amount" fullWidth />
						</Grid>
						<Grid item xs>
							<ReferenceInput source="limitValueAmount.currency" reference="currencies">
								<SelectInput optionText="currencyCode" resettable fullWidth />
							</ReferenceInput>
						</Grid>
					</Grid>
					<TextInput source="customType" fullWidth />
					<TextInput source="description" fullWidth />
				</Grid>
			</Grid>
		</SimpleForm>
	</Edit>
);

LimitDefinitionEdit.propTypes = {
	limitContextsForSelection: PropTypes.array.isRequired,
	walletTypesForSelection: PropTypes.array.isRequired,
	verificationLevelsForSelection: PropTypes.array.isRequired,
	limitTypesForSelection: PropTypes.array.isRequired,
	limitCurrencyTypesForSelection: PropTypes.array.isRequired,
	limitSubtypesForSelection: PropTypes.array.isRequired,
	limitScopesForSelection: PropTypes.array.isRequired,
	limitPeriodsForSelection: PropTypes.array.isRequired,
	limitOperationsForSelection: PropTypes.array.isRequired
};
LimitDefinitionEdit.defaultProps = {
	limitContextsForSelection: coreWalletLimitContextsForSelection,
	walletTypesForSelection: coreWalletTypesForSelection,
	verificationLevelsForSelection: coreWalletVerificationLevelsForSelection,
	limitTypesForSelection: coreWalletLimitTypesForSelection,
	limitCurrencyTypesForSelection: coreWalletLimitCurrencyTypesForSelection,
	limitSubtypesForSelection: coreWalletLimitSubtypesForSelection,
	limitScopesForSelection: coreWalletLimitScopesForSelection,
	limitPeriodsForSelection: coreWalletLimitPeriodsForSelection,
	limitOperationsForSelection: coreWalletLimitOperationsForSelection
};

export default LimitDefinitionEdit;
